import React from 'react'
import { Modal, Button, Typography, Box, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'

interface SeasonCloseConfirmationModalProps {
	open: boolean
	onClose: () => void
	onConfirm: () => void
	seasonTitle: string
}

/**
 * 판매 중지 확인.
 *
 * 중지하면 판매중인 시즌이 없어지고, shop 은 빈 목록을 받아 **전 상품이 Sold Out** 이 된다.
 * 되돌리려면 다시 판매 시작을 눌러야 하고 그때 판매 기간 안이어야 한다 —
 * 기간이 지난 시즌은 다시 열 수 없다. (SEASON_OPEN_PLAN §4)
 */
export const SeasonCloseConfirmationModal: React.FC<
	SeasonCloseConfirmationModalProps
> = ({ open, onClose, onConfirm, seasonTitle }) => {
	return (
		<Modal
			open={open}
			onClose={onClose}
			aria-labelledby="close-confirmation-title"
			aria-describedby="close-confirmation-description"
		>
			<Box component="div" sx={styles.modalContent}>
				<IconButton onClick={onClose} sx={styles.closeButton} aria-label="close">
					<CloseIcon />
				</IconButton>
				<StorefrontOutlinedIcon sx={styles.icon} />
				<Typography
					id="close-confirmation-title"
					variant="h6"
					component="h2"
					sx={styles.title}
				>
					판매 중지 확인
				</Typography>
				<Typography
					id="close-confirmation-description"
					variant="body1"
					sx={styles.description}
				>
					<strong>{seasonTitle}</strong> 시즌의 판매를 중지합니다.
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={styles.warning}>
					shop 의 전 상품이 Sold Out 으로 바뀝니다. 다시 열려면 판매 기간 안이어야 합니다.
				</Typography>
				<Box sx={styles.buttonContainer}>
					<Button variant="text" onClick={onClose} sx={styles.cancelButton}>
						취소
					</Button>
					<Button variant="contained" onClick={onConfirm} sx={styles.confirmButton}>
						판매 중지
					</Button>
				</Box>
			</Box>
		</Modal>
	)
}

const styles = {
	modalContent: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		width: '400px',
		maxWidth: 'calc(100vw - 32px)',
		backgroundColor: mono.surface,
		border: `1px solid ${mono.line}`,
		boxShadow: '0 30px 60px rgba(20, 24, 32, 0.12)',
		padding: '32px',
		borderRadius: 0,
		textAlign: 'left',
		fontFamily: boFont.ui,
		outline: 'none',
	},
	closeButton: {
		position: 'absolute',
		right: '8px',
		top: '8px',
	},
	icon: {
		fontSize: '28px',
		color: mono.dim,
		marginBottom: '16px',
	},
	title: {
		marginBottom: '12px',
		fontSize: 18,
		fontWeight: 400,
		color: mono.ink,
	},
	description: {
		marginBottom: '8px',
		fontSize: 14,
		fontWeight: 300,
		color: mono.ink,
		lineHeight: 1.6,
	},
	warning: {
		marginBottom: '24px',
		fontSize: 13,
		fontWeight: 300,
		color: mono.dim,
		lineHeight: 1.6,
	},
	buttonContainer: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: '8px',
		paddingTop: '16px',
		borderTop: `1px solid ${mono.line}`,
	},
	cancelButton: {
		minWidth: '80px',
	},
	confirmButton: {
		minWidth: '100px',
	},
	deleteButton: {
		minWidth: '100px',
	},
}
